import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../state/auth.js';
import { useCart } from '../state/cart.js';
import { useTheme } from '../lib/hooks.js';
import { Badge, Button } from './ui.js';
import { CartDrawer } from './CartDrawer.js';

/**
 * Application shell.
 *
 * The header is sticky rather than fixed: a fixed header has to be compensated
 * for with padding on every page, and that compensation is the first thing to
 * break when a page adds its own layout. Sticky needs no compensation and gives
 * the same behaviour.
 */

const NAV = [
  { to: '/', label: 'Catalogue', end: true },
  { to: '/orders', label: 'Orders', end: false },
];

export function Layout() {
  const { user, status, signOut, isStaff } = useAuth();
  const { itemCount, openDrawer } = useCart();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-accent focus:px-3 focus:py-2 focus:text-accent-ink"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-30 border-b border-line bg-canvas/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2.5" aria-label="Harbor home">
            <HarborMark />
            <span className="text-base font-semibold tracking-tight">Harbor</span>
          </Link>

          <nav className="ml-4 hidden items-center gap-1 md:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive ? 'bg-raised text-ink' : 'text-muted hover:text-ink',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
            {isStaff && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  [
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive ? 'bg-raised text-ink' : 'text-muted hover:text-ink',
                  ].join(' ')
                }
              >
                Admin
              </NavLink>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-1.5">
            <IconButton label="Toggle theme" onClick={toggle}>
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </IconButton>

            <button
              type="button"
              onClick={openDrawer}
              className="relative rounded-lg p-2.5 text-muted transition-colors hover:bg-raised hover:text-ink"
              aria-label={`Basket, ${itemCount} item${itemCount === 1 ? '' : 's'}`}
            >
              <BagIcon />
              {itemCount > 0 && (
                <span className="tabular absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-semibold text-accent-ink">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </button>

            {status === 'loading' ? (
              <div className="size-9 animate-pulse rounded-full bg-raised" />
            ) : user ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  className="flex items-center gap-2 rounded-lg p-1.5 pr-2.5 text-sm transition-colors hover:bg-raised"
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                >
                  <span className="flex size-7 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
                    {initials(user.name)}
                  </span>
                  <span className="hidden max-w-24 truncate sm:block">{user.name}</span>
                </button>

                {menuOpen && (
                  <>
                    {/* Click-outside layer. Rendered as a sibling rather than a
                        document listener so it cannot swallow clicks on the menu. */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setMenuOpen(false)}
                      aria-hidden="true"
                    />
                    <div
                      role="menu"
                      className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-line bg-surface shadow-[0_1px_2px_var(--shadow-colour),0_12px_32px_-12px_var(--shadow-colour)]"
                    >
                      <div className="border-b border-line px-3 py-2.5">
                        <p className="truncate text-sm font-medium">{user.name}</p>
                        <p className="truncate text-xs text-subtle">{user.email}</p>
                      </div>
                      <div className="p-1">
                        <MenuItem
                          onClick={() => {
                            setMenuOpen(false);
                            navigate('/account');
                          }}
                        >
                          Account and sessions
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setMenuOpen(false);
                            navigate('/orders');
                          }}
                        >
                          Order history
                        </MenuItem>
                        {isStaff && (
                          <MenuItem
                            onClick={() => {
                              setMenuOpen(false);
                              navigate('/admin');
                            }}
                          >
                            Admin console
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            setMenuOpen(false);
                            void signOut();
                          }}
                          tone="danger"
                        >
                          Sign out
                        </MenuItem>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                  Sign in
                </Button>
                <Button size="sm" onClick={() => navigate('/register')}>
                  Create account
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <Footer />

      <CartDrawer />
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  tone = 'neutral',
}: {
  children: ReactNode;
  onClick: () => void;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={[
        'block w-full rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
        tone === 'danger' ? 'text-danger hover:bg-danger-soft' : 'text-ink hover:bg-raised',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Footer() {
  return (
    <footer className="mt-20 border-t border-line">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-4 lg:px-8">
        <div className="space-y-3 md:col-span-2">
          <div className="flex items-center gap-2.5">
            <HarborMark />
            <span className="font-semibold tracking-tight">Harbor</span>
          </div>
          <p className="max-w-sm text-sm text-muted">
            Considered furniture for people who spend their working life at a desk. Built to be
            repaired rather than replaced, and priced without a showroom in the middle.
          </p>
          <Badge tone="accent">Runs on Cloudflare Workers, D1 and R2</Badge>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-subtle">Shop</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link to="/?category=desks" className="text-muted transition-colors hover:text-ink">
                Desks
              </Link>
            </li>
            <li>
              <Link to="/?category=seating" className="text-muted transition-colors hover:text-ink">
                Seating
              </Link>
            </li>
            <li>
              <Link to="/?category=lighting" className="text-muted transition-colors hover:text-ink">
                Lighting
              </Link>
            </li>
            <li>
              <Link to="/?category=audio" className="text-muted transition-colors hover:text-ink">
                Audio
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-subtle">Account</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link to="/orders" className="text-muted transition-colors hover:text-ink">
                Order history
              </Link>
            </li>
            <li>
              <Link to="/account" className="text-muted transition-colors hover:text-ink">
                Sessions and security
              </Link>
            </li>
            <li>
              <a
                href="https://github.com/rahilakbar/harbor-commerce"
                className="text-muted transition-colors hover:text-ink"
                rel="noreferrer noopener"
                target="_blank"
              >
                Source code
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-line">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-6 text-xs text-subtle sm:px-6 lg:px-8">
          <p>A demonstration storefront. No real payments are processed and no orders ship.</p>
          <p className="tabular">MIT licensed</p>
        </div>
      </div>
    </footer>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-lg p-2.5 text-muted transition-colors hover:bg-raised hover:text-ink"
    >
      {children}
    </button>
  );
}

function HarborMark() {
  return (
    <svg viewBox="0 0 32 32" className="size-7 text-accent" aria-hidden="true" fill="none">
      <rect x="2" y="2" width="28" height="28" rx="8" stroke="currentColor" strokeWidth="2" />
      <path d="M10 22V10m0 6h12m0 6V10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function BagIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M6 8h12l-1 12H7L6 8Z" strokeLinejoin="round" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" strokeLinecap="round" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.75">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10 1.4 1.4m0-12.8-1.4 1.4m-10 10-1.4 1.4" strokeLinecap="round" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" strokeLinejoin="round" />
    </svg>
  );
}
